import { useState, useEffect, useRef } from "react";
import { useHookWaDevice } from "../../../../../hooks/useHookWaDevice";
import { useNavigate } from "react-router-dom";
import {
    FaQrcode,
    FaXmark,
    FaRotate,
    FaRightFromBracket,
    FaPenToSquare,
    FaCheck,
    FaMobileScreenButton,
    FaWhatsapp,
    FaCircleExclamation,
    FaTrashCan,
    FaCircleCheck,
} from "react-icons/fa6";
import { WaDeviceService } from "../../../../../services/WaDeviceService";

export const WaDeviceSection = () => {
    const { data, loading, refreshData } = useHookWaDevice();
    const navigate = useNavigate();

    const [selectedIds, setSelectedIds] = useState([]);

    // ==========================================
    // REFS UNTUK QR POLLING
    // ==========================================
    const scanPollRef = useRef(null);
    const isScanModalOpenRef = useRef(false);

    useEffect(() => {
        return () => {
            if (scanPollRef.current) clearTimeout(scanPollRef.current);
        };
    }, []);

    // ==========================================
    // STATE UNTUK POPUP QR SCANNER
    // ==========================================
    const [scanModal, setScanModal] = useState({
        isOpen: false,
        deviceId: null,
        deviceName: "",
        deviceNumber: null,
        qrImage: null,
        qrLoading: false,
        qrMessage: "",
        isScanned: false,
    });

    // ==========================================
    // STATE UNTUK POPUP KONFIRMASI LOGOUT
    // ==========================================
    const [logoutModal, setLogoutModal] = useState({
        isOpen: false,
        deviceId: null,
        deviceNumber: null,
        deviceName: "",
        isLoggingOut: false,
    });

    // ==========================================
    // STATE UNTUK POPUP UPDATE DEVICE
    // ==========================================
    const [updateModal, setUpdateModal] = useState({
        isOpen: false,
        deviceId: null,
        deviceName: "",
        newName: "",
        isUpdating: false,
    });

    // ==========================================
    // STATE UNTUK POPUP HAPUS DEVICE
    // ==========================================
    const [deleteModal, setDeleteModal] = useState({
        isOpen: false,
        deviceId: null,
        deviceName: "",
        isDeleting: false,
    });

    const deviceColumns = [
        { key: "no", label: "No", align: "center", width: "50px" },
        { key: "name", label: "Nama Perangkat", align: "left" },
        { key: "number", label: "Nomor WhatsApp", align: "left" },
        { key: "status", label: "Status", align: "center", width: "130px" },
        { key: "action", label: "Aksi", align: "center", width: "320px" },
    ];

    // ==========================================
    // FUNGSI QR CODE — POLLING KE BACKEND
    // ==========================================
    const handleOpenScan = (item) => {
        if (scanPollRef.current) {
            clearTimeout(scanPollRef.current);
            scanPollRef.current = null;
        }

        isScanModalOpenRef.current = true;

        setScanModal({
            isOpen: true,
            deviceId: item.id,
            deviceName: item.name || "Device",
            deviceNumber: item.number,
            qrImage: null,
            qrLoading: true,
            qrMessage: "Membuat QR Code...",
            isScanned: false,
        });

        startQRPolling(item.number);
    };

    const startQRPolling = (deviceNumber) => {
        let attempts = 0;
        const maxAttempts = 30;
        const delay = 2000;
    
        const poll = async () => {
            if (!isScanModalOpenRef.current || attempts >= maxAttempts) {
                if (attempts >= maxAttempts && isScanModalOpenRef.current) {
                    setScanModal((prev) => ({
                        ...prev,
                        qrLoading: false,
                        qrMessage: "Timeout. Gagal mendapatkan QR Code.",
                    }));
                }
                return;
            }
    
            attempts++;
            console.log(`[QR Poll] Percobaan ${attempts}/${maxAttempts}`);
    
            try {
                const res = await WaDeviceService.generateQR({
                    params: { device: deviceNumber },
                });
    
                if (!isScanModalOpenRef.current) return;
                console.log("[QR Poll] Response:", res.status, res.message || res.msg);
    
                // ── 1. Masih processing → auto get ulang ──
                if (res.status === "processing") {
                    setScanModal((prev) => ({
                        ...prev,
                        qrMessage: `Memproses QR Code... (${attempts})`,
                    }));
                    scanPollRef.current = setTimeout(poll, delay);
                    return;
                }

                console.log("+++++++++++++++++++", res.data.message);
    
                // ── 2. QR Code siap (base64) → tampilkan ──
                if (res.data.message === "please scan") {
                    console.log("[QR Poll] ✅ QR Code diterima");
                    setScanModal((prev) => ({
                        ...prev,
                        qrImage: res.data.qrcode,
                        qrLoading: false,
                        qrMessage: "Silakan scan QR Code dengan WhatsApp Anda",
                    }));
                    startScanDetection(deviceNumber);
                    return;
                }
                
    
                // ── 3. Sudah scan → sukses ──
                const msg = (res.msg || res.message || "").toLowerCase();
                if (msg.includes("already connected")) {
                    console.log("[QR Poll] ✅ Device sudah terhubung");
                    handleScanSuccess();
                    return;
                }
    
                // ── 4. Response lain / error ──
                console.warn("[QR Poll] Response tidak dikenali:", res);
                setScanModal((prev) => ({
                    ...prev,
                    qrLoading: false,
                    qrMessage: res.msg || res.message || "Gagal mendapatkan QR Code",
                }));
            } catch (error) {
                console.error("[QR Poll] Error:", error);
                if (!isScanModalOpenRef.current) return;
    
                if (attempts < maxAttempts) {
                    scanPollRef.current = setTimeout(poll, delay);
                } else {
                    setScanModal((prev) => ({
                        ...prev,
                        qrLoading: false,
                        qrMessage: "Gagal terhubung ke server. Coba lagi.",
                    }));
                }
            }
        };
    
        poll();
    };
    
    const startScanDetection = (deviceNumber) => {
        let attempts = 0;
        const maxAttempts = 90;
        const delay = 2000;

        const detect = async () => {
            if (!isScanModalOpenRef.current || attempts >= maxAttempts) {
                if (attempts >= maxAttempts && isScanModalOpenRef.current) {
                    setScanModal((prev) => ({
                        ...prev,
                        qrMessage:
                            "QR Code kadaluarsa. Klik Refresh untuk mendapatkan yang baru.",
                    }));
                }
                return;
            }
            attempts++;

            try {
                const res = await WaDeviceService.generateQR({
                    params: { device: deviceNumber },
                });
                if (!isScanModalOpenRef.current) return;

                if (res.msg?.toLowerCase().includes("already connected")) {
                    handleScanSuccess();
                    return;
                }

                scanPollRef.current = setTimeout(detect, delay);
            } catch {
                scanPollRef.current = setTimeout(detect, delay);
            }
        };

        scanPollRef.current = setTimeout(detect, delay);
    };

    const handleScanSuccess = () => {
        if (scanPollRef.current) {
            clearTimeout(scanPollRef.current);
            scanPollRef.current = null;
        }

        setScanModal((prev) => ({
            ...prev,
            qrLoading: false,
            qrImage: null,
            qrMessage: "Perangkat berhasil terhubung!",
            isScanned: true,
        }));

        setTimeout(() => {
            handleCloseScanModal();
            refreshData();
        }, 1500);
    };

    const handleCloseScanModal = () => {
        isScanModalOpenRef.current = false;
        if (scanPollRef.current) {
            clearTimeout(scanPollRef.current);
            scanPollRef.current = null;
        }
        setScanModal({
            isOpen: false,
            deviceId: null,
            deviceName: "",
            deviceNumber: null,
            qrImage: null,
            qrLoading: false,
            qrMessage: "",
            isScanned: false,
        });
    };

    // ==========================================
    // FUNGSI LOGOUT
    // ==========================================
    const handleOpenLogout = (item) => {
        setLogoutModal({
            isOpen: true,
            deviceId: item.id,
            deviceNumber: item.number,
            deviceName: item.name || "Device",
            isLoggingOut: false,
        });
    };

    const handleConfirmLogout = async () => {
        setLogoutModal((prev) => ({ ...prev, isLoggingOut: true }));
        try {
            await WaDeviceService.logout({
                data: { device: logoutModal.deviceNumber },
            });
            setLogoutModal({
                isOpen: false,
                deviceId: null,
                deviceNumber: null,
                deviceName: "",
                isLoggingOut: false,
            });
            refreshData();
        } catch (error) {
            alert("Gagal logout device");
            setLogoutModal((prev) => ({ ...prev, isLoggingOut: false }));
        }
    };

    const handleCloseLogoutModal = () => {
        setLogoutModal({
            isOpen: false,
            deviceId: null,
            deviceNumber: null,
            deviceName: "",
            isLoggingOut: false,
        });
    };

    // ==========================================
    // FUNGSI UPDATE
    // ==========================================
    const handleOpenUpdate = (item) => {
        setUpdateModal({
            isOpen: true,
            deviceId: item.id,
            deviceName: item.name || "Device",
            newName: item.name || "",
            isUpdating: false,
        });
    };

    const handleConfirmUpdate = async () => {
        const trimmed = updateModal.newName.trim();
        if (!trimmed) return;

        setUpdateModal((prev) => ({ ...prev, isUpdating: true }));
        try {
            await WaDeviceService.update(updateModal.deviceId, {
                name: trimmed,
            });
            setUpdateModal({
                isOpen: false,
                deviceId: null,
                deviceName: "",
                newName: "",
                isUpdating: false,
            });
            refreshData();
        } catch (error) {
            alert("Gagal memperbarui nama device");
            setUpdateModal((prev) => ({ ...prev, isUpdating: false }));
        }
    };

    const handleCloseUpdateModal = () => {
        setUpdateModal({
            isOpen: false,
            deviceId: null,
            deviceName: "",
            newName: "",
            isUpdating: false,
        });
    };

    // ==========================================
    // FUNGSI HAPUS DEVICE
    // ==========================================
    const handleOpenDelete = (item) => {
        setDeleteModal({
            isOpen: true,
            deviceId: item.id,
            deviceName: item.name || "Device",
            isDeleting: false,
        });
    };

    const handleConfirmDelete = async () => {
        setDeleteModal((prev) => ({ ...prev, isDeleting: true }));
        try {
            await WaDeviceService.delete(deleteModal.deviceId);
            setDeleteModal({
                isOpen: false,
                deviceId: null,
                deviceName: "",
                isDeleting: false,
            });
            refreshData();
        } catch (error) {
            alert("Gagal menghapus device");
            setDeleteModal((prev) => ({ ...prev, isDeleting: false }));
        }
    };

    const handleCloseDeleteModal = () => {
        setDeleteModal({
            isOpen: false,
            deviceId: null,
            deviceName: "",
            isDeleting: false,
        });
    };

    // ==========================================
    // SELECT HANDLER
    // ==========================================
    const handleToggleSelect = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const handleSelectAll = () => {
        if (!data || data.length === 0) return;
        if (selectedIds.length === data.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(data.map((d) => d.id));
        }
    };

    // ==========================================
    // HELPER: FORMAT NOMOR WA
    // ==========================================
    const formatPhoneNumber = (num) => {
        if (!num) return "-";
        const cleaned = num.replace(/\D/g, "");
        if (cleaned.startsWith("62") && cleaned.length > 10) {
            return `+${cleaned.slice(0, 4)} ${cleaned.slice(4, 8)}-${cleaned.slice(8)}`;
        }
        if (cleaned.startsWith("0") && cleaned.length > 9) {
            return `${cleaned.slice(0, 4)} ${cleaned.slice(4, 8)}-${cleaned.slice(8)}`;
        }
        return num;
    };

    // ==========================================
    // MODAL OVERLAY WRAPPER
    // ==========================================
    const ModalOverlay = ({ children, onClose }) => (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={onClose}
            />
            {children}
        </div>
    );

    const modalBoxStyle = {
        background: "rgba(15, 23, 42, 0.97)",
        borderColor: "rgba(51, 65, 85, 0.5)",
    };

    const modalAnim = `
        @keyframes modalIn {
            from { opacity: 0; transform: scale(0.92) translateY(16px); }
            to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes successPop {
            0%   { transform: scale(0); opacity: 0; }
            60%  { transform: scale(1.15); opacity: 1; }
            100% { transform: scale(1); opacity: 1; }
        }
    `;

    // ==========================================
    // KOMPONEN POPUP SCAN QR
    // ==========================================
    const ScanModal = () => {
        if (!scanModal.isOpen) return null;

        return (
            <ModalOverlay onClose={handleCloseScanModal}>
                <style>{modalAnim}</style>
                <div
                    className="relative w-full max-w-sm rounded-2xl p-6 shadow-2xl border"
                    style={{
                        ...modalBoxStyle,
                        animation: "modalIn 0.25s ease-out",
                    }}
                >
                    <button
                        onClick={handleCloseScanModal}
                        className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-all"
                    >
                        <FaXmark size={16} />
                    </button>

                    <div className="text-center">
                        {/* Header Icon */}
                        <div
                            className={`w-12 h-12 mx-auto mb-4 rounded-xl flex items-center justify-center border transition-colors duration-300 ${
                                scanModal.isScanned
                                    ? "bg-emerald-500/10 border-emerald-500/20"
                                    : "bg-cyan-500/10 border-cyan-500/20"
                            }`}
                        >
                            {scanModal.isScanned ? (
                                <FaCircleCheck className="text-emerald-400 text-lg" />
                            ) : (
                                <FaQrcode className="text-cyan-400 text-lg" />
                            )}
                        </div>

                        <h3 className="text-lg font-bold text-gray-100 mb-1">
                            {scanModal.deviceName}
                        </h3>

                        {/* Pesan Status */}
                        <p
                            className={`text-xs mb-6 px-2 leading-relaxed transition-colors duration-300 ${
                                scanModal.isScanned
                                    ? "text-emerald-400 font-semibold"
                                    : "text-slate-400"
                            }`}
                        >
                            {scanModal.qrMessage}
                        </p>

                        {/* Konten Utama: QR / Error / Success */}
                        {scanModal.isScanned ? (
                            /* — SUKSES — */
                            <div className="w-56 h-56 mx-auto mb-6 flex items-center justify-center">
                                <FaCircleCheck
                                    className="text-emerald-400"
                                    style={{
                                        fontSize: "80px",
                                        animation: "successPop 0.5s ease-out",
                                    }}
                                />
                            </div>
                        ) : (
                            /* — QR / LOADING / ERROR — */
                            <div className="w-56 h-56 mx-auto bg-white rounded-2xl p-3 flex items-center justify-center mb-6 relative overflow-hidden shadow-lg shadow-black/20">
                                {scanModal.qrLoading && (
                                    <div className="absolute inset-0 bg-white flex flex-col items-center justify-center z-10 rounded-2xl">
                                        <div className="w-8 h-8 border-[3px] border-cyan-500 border-t-transparent rounded-full animate-spin mb-3" />
                                        <span className="text-slate-500 text-xs font-medium">
                                            Memproses...
                                        </span>
                                    </div>
                                )}
                                {scanModal.qrImage && !scanModal.qrLoading && (
                                    <img
                                        src={scanModal.qrImage}
                                        alt="QR Code"
                                        className="w-full h-full object-contain rounded-lg"
                                    />
                                )}
                                {!scanModal.qrLoading &&
                                    !scanModal.qrImage && (
                                        <div className="flex flex-col items-center justify-center text-center px-4">
                                            <FaCircleExclamation className="text-slate-300 text-2xl mb-2" />
                                            <span className="text-slate-500 text-xs">
                                                {scanModal.qrMessage ||
                                                    "Terjadi kesalahan"}
                                            </span>
                                        </div>
                                    )}
                            </div>
                        )}

                        {/* Tombol Aksi (tidak ditampilkan saat success) */}
                        {!scanModal.isScanned && (
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() =>
                                        handleOpenScan({
                                            id: scanModal.deviceId,
                                            name: scanModal.deviceName,
                                            number: scanModal.deviceNumber,
                                        })
                                    }
                                    disabled={scanModal.qrLoading}
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold transition-all bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    <FaRotate size={11} /> Refresh
                                </button>
                                <button
                                    onClick={handleCloseScanModal}
                                    className="flex-1 px-4 py-2.5 rounded-xl text-xs font-semibold transition-all bg-red-500/8 text-red-400 hover:bg-red-500/15 border border-red-500/15"
                                >
                                    Tutup
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </ModalOverlay>
        );
    };

    // ==========================================
    // KOMPONEN POPUP KONFIRMASI LOGOUT
    // ==========================================
    const LogoutModal = () => {
        if (!logoutModal.isOpen) return null;
        return (
            <ModalOverlay onClose={handleCloseLogoutModal}>
                <style>{modalAnim}</style>
                <div
                    className="relative w-full max-w-xs rounded-2xl p-6 shadow-2xl border"
                    style={{
                        ...modalBoxStyle,
                        animation: "modalIn 0.25s ease-out",
                    }}
                >
                    <div className="text-center">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-orange-500/8 border border-orange-500/15 flex items-center justify-center">
                            <FaRightFromBracket className="text-orange-400 text-2xl" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-100 mb-2">
                            Logout Device?
                        </h3>
                        <p className="text-sm text-slate-400 mb-6 leading-relaxed">
                            Putuskan koneksi{" "}
                            <span className="text-white font-semibold">
                                {logoutModal.deviceName}
                            </span>{" "}
                            dari server?
                        </p>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleCloseLogoutModal}
                                disabled={logoutModal.isLoggingOut}
                                className="flex-1 px-4 py-2.5 rounded-xl text-xs font-semibold transition-all bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700 disabled:opacity-40"
                            >
                                Batal
                            </button>
                            <button
                                onClick={handleConfirmLogout}
                                disabled={logoutModal.isLoggingOut}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold transition-all bg-orange-600 text-white hover:bg-orange-500 disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-orange-600/20"
                            >
                                {logoutModal.isLoggingOut ? (
                                    <>
                                        <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        Memutus...
                                    </>
                                ) : (
                                    "Ya, Logout"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </ModalOverlay>
        );
    };

    // ==========================================
    // KOMPONEN POPUP UPDATE DEVICE
    // ==========================================
    const UpdateModal = () => {
        if (!updateModal.isOpen) return null;
        return (
            <ModalOverlay onClose={handleCloseUpdateModal}>
                <style>{modalAnim}</style>
                <div
                    className="relative w-full max-w-sm rounded-2xl p-6 shadow-2xl border"
                    style={{
                        ...modalBoxStyle,
                        animation: "modalIn 0.25s ease-out",
                    }}
                >
                    <button
                        onClick={handleCloseUpdateModal}
                        className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-all"
                    >
                        <FaXmark size={16} />
                    </button>
                    <div>
                        <div className="flex items-center gap-3 mb-5">
                            <div className="w-11 h-11 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center shrink-0">
                                <FaPenToSquare className="text-blue-400 text-base" />
                            </div>
                            <div>
                                <h3 className="text-base font-bold text-gray-100">
                                    Update Perangkat
                                </h3>
                                <p className="text-[11px] text-slate-500">
                                    Mengubah nama "{updateModal.deviceName}"
                                </p>
                            </div>
                        </div>
                        <label className="block mb-5">
                            <span className="block text-xs font-semibold text-slate-400 mb-1.5">
                                Nama Perangkat
                            </span>
                            <input
                                type="text"
                                value={updateModal.newName}
                                onChange={(e) =>
                                    setUpdateModal((prev) => ({
                                        ...prev,
                                        newName: e.target.value,
                                    }))
                                }
                                onKeyDown={(e) =>
                                    e.key === "Enter" && handleConfirmUpdate()
                                }
                                placeholder="Masukkan nama baru..."
                                autoFocus
                                className="w-full px-4 py-2.5 rounded-xl text-sm text-gray-100 placeholder-slate-600 bg-slate-800/80 border border-slate-700 focus:border-cyan-500/50 focus:ring-2 focus:ring-cyan-500/10 outline-none transition-all"
                            />
                            {!updateModal.newName.trim() && (
                                <p className="mt-1.5 text-[10px] text-amber-400/80 flex items-center gap-1">
                                    <FaCircleExclamation size={10} /> Nama
                                    tidak boleh kosong
                                </p>
                            )}
                        </label>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleCloseUpdateModal}
                                disabled={updateModal.isUpdating}
                                className="flex-1 px-4 py-2.5 rounded-xl text-xs font-semibold transition-all bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700 disabled:opacity-40"
                            >
                                Batal
                            </button>
                            <button
                                onClick={handleConfirmUpdate}
                                disabled={
                                    updateModal.isUpdating ||
                                    !updateModal.newName.trim()
                                }
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold transition-all bg-cyan-600 text-white hover:bg-cyan-500 disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-cyan-600/20"
                            >
                                {updateModal.isUpdating ? (
                                    <>
                                        <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        Menyimpan...
                                    </>
                                ) : (
                                    <>
                                        <FaCheck size={11} /> Simpan
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </ModalOverlay>
        );
    };

    // ==========================================
    // KOMPONEN POPUP HAPUS DEVICE
    // ==========================================
    const DeleteModal = () => {
        if (!deleteModal.isOpen) return null;
        return (
            <ModalOverlay onClose={handleCloseDeleteModal}>
                <style>{modalAnim}</style>
                <div
                    className="relative w-full max-w-xs rounded-2xl p-6 shadow-2xl border"
                    style={{
                        ...modalBoxStyle,
                        animation: "modalIn 0.25s ease-out",
                    }}
                >
                    <div className="text-center">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red-500/8 border border-red-500/15 flex items-center justify-center">
                            <FaTrashCan className="text-red-400 text-2xl" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-100 mb-2">
                            Hapus Device?
                        </h3>
                        <p className="text-sm text-slate-400 mb-1 leading-relaxed">
                            Hapus perangkat{" "}
                            <span className="text-white font-semibold">
                                {deleteModal.deviceName}
                            </span>{" "}
                            dari sistem?
                        </p>
                        <p className="text-[11px] text-red-400/70 mb-6">
                            Tindakan ini tidak dapat dibatalkan.
                        </p>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleCloseDeleteModal}
                                disabled={deleteModal.isDeleting}
                                className="flex-1 px-4 py-2.5 rounded-xl text-xs font-semibold transition-all bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700 disabled:opacity-40"
                            >
                                Batal
                            </button>
                            <button
                                onClick={handleConfirmDelete}
                                disabled={deleteModal.isDeleting}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold transition-all bg-red-600 text-white hover:bg-red-500 disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-red-600/20"
                            >
                                {deleteModal.isDeleting ? (
                                    <>
                                        <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        Menghapus...
                                    </>
                                ) : (
                                    "Ya, Hapus"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </ModalOverlay>
        );
    };

    // ==========================================
    // SKELETON LOADING
    // ==========================================
    const TableSkeleton = () => (
        <tbody>
            {Array.from({ length: 4 }).map((_, i) => (
                <tr key={i} className="border-b border-slate-800/50">
                    <td className="px-4 py-4 text-center">
                        <div className="w-4 h-4 mx-auto rounded bg-slate-700/40 animate-pulse" />
                    </td>
                    <td className="px-4 py-4 text-center">
                        <div className="w-6 h-4 mx-auto rounded bg-slate-700/40 animate-pulse" />
                    </td>
                    <td className="px-4 py-4">
                        <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-lg bg-slate-700/40 animate-pulse" />
                            <div className="w-28 h-4 rounded bg-slate-700/40 animate-pulse" />
                        </div>
                    </td>
                    <td className="px-4 py-4">
                        <div className="w-32 h-4 rounded bg-slate-700/40 animate-pulse" />
                    </td>
                    <td className="px-4 py-4 text-center">
                        <div className="w-20 h-6 mx-auto rounded-full bg-slate-700/40 animate-pulse" />
                    </td>
                    <td className="px-4 py-4 text-center">
                        <div className="inline-grid grid-cols-4 gap-1">
                            <div className="w-[68px] h-7 rounded-lg bg-slate-700/40 animate-pulse" />
                            <div className="w-[68px] h-7 rounded-lg bg-slate-700/40 animate-pulse" />
                            <div className="w-[68px] h-7 rounded-lg bg-slate-700/40 animate-pulse" />
                            <div className="w-[68px] h-7 rounded-lg bg-slate-700/40 animate-pulse" />
                        </div>
                    </td>
                </tr>
            ))}
        </tbody>
    );

    const CardSkeleton = () => (
        <div className="rounded-2xl p-4 border border-slate-800/50 bg-slate-900/40 animate-pulse">
            <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-slate-700/40" />
                    <div className="space-y-2">
                        <div className="w-24 h-4 rounded bg-slate-700/40" />
                        <div className="w-32 h-3 rounded bg-slate-700/30" />
                    </div>
                </div>
                <div className="w-16 h-6 rounded-full bg-slate-700/40" />
            </div>
            <div className="grid grid-cols-4 gap-2">
                <div className="h-9 rounded-xl bg-slate-700/30" />
                <div className="h-9 rounded-xl bg-slate-700/30" />
                <div className="h-9 rounded-xl bg-slate-700/30" />
                <div className="h-9 rounded-xl bg-slate-700/30" />
            </div>
        </div>
    );

    // ==========================================
    // EMPTY STATE
    // ==========================================
    const EmptyState = () => (
        <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="w-20 h-20 rounded-2xl bg-slate-800/60 border border-slate-700/40 flex items-center justify-center mb-5">
                <FaMobileScreenButton className="text-slate-600 text-3xl" />
            </div>
            <h3 className="text-base font-bold text-slate-400 mb-1">
                Belum Ada Perangkat
            </h3>
            <p className="text-xs text-slate-600 text-center max-w-xs">
                Hubungkan perangkat WhatsApp pertama Anda untuk mulai mengirim
                pesan.
            </p>
        </div>
    );

    // ==========================================
    // STATUS BADGE
    // ==========================================
    const StatusBadge = ({ status }) => {
        const isConnected = status === "Connected";
        return (
            <span
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold tracking-wide ${
                    isConnected
                        ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                        : "bg-slate-700/40 text-slate-400 border border-slate-700/50"
                }`}
            >
                <span
                    className={`w-1.5 h-1.5 rounded-full ${
                        isConnected
                            ? "bg-emerald-400 shadow-sm shadow-emerald-400/50"
                            : "bg-slate-500"
                    }`}
                />
                {isConnected ? "Terhubung" : "Terputus"}
            </span>
        );
    };

    // ==========================================
    // ACTION BUTTON — FIXED WIDTH
    // ==========================================
    const ActionBtn = ({
        icon: Icon,
        label,
        color,
        onClick,
        disabled,
    }) => {
        const colors = {
            cyan: "bg-cyan-500/8 text-cyan-400 border-cyan-500/15 hover:bg-cyan-500/15 hover:border-cyan-500/25",
            amber: "bg-amber-500/8 text-amber-400 border-amber-500/15 hover:bg-amber-500/15 hover:border-amber-500/25",
            red: "bg-red-500/8 text-red-400 border-red-500/15 hover:bg-red-500/15 hover:border-red-500/25",
            blue: "bg-blue-500/8 text-blue-400 border-blue-500/15 hover:bg-blue-500/15 hover:border-blue-500/25",
            slate: "bg-slate-700/30 text-slate-400 border-slate-600/30 hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/20",
        };
        return (
            <button
                onClick={onClick}
                disabled={disabled}
                className={`w-full flex items-center justify-center gap-1 px-1 py-[6px] rounded-lg text-[10px] font-bold transition-all border whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed ${colors[color]}`}
            >
                <Icon size={10} className="shrink-0" />
                <span>{label}</span>
            </button>
        );
    };

    // ==========================================
    // ACTION GROUP — 4 SLOT GRID
    // ==========================================
    const ActionGroup = ({ item }) => {
        const isConnected = item?.status === "Connected";
        return (
            <div className="grid grid-cols-4 gap-1 w-full">
                {/* Slot 1: Scan */}
                <ActionBtn
                    icon={FaQrcode}
                    label={isConnected ? "Scan Ulang" : "Scan"}
                    color={isConnected ? "amber" : "cyan"}
                    onClick={() => handleOpenScan(item)}
                />

                {/* Slot 2: Update */}
                <ActionBtn
                    icon={FaPenToSquare}
                    label="Update"
                    color="blue"
                    onClick={() => handleOpenUpdate(item)}
                />

                {/* Slot 3: Logout / Placeholder */}
                {isConnected ? (
                    <ActionBtn
                        icon={FaRightFromBracket}
                        label="Logout"
                        color="amber"
                        onClick={() => handleOpenLogout(item)}
                    />
                ) : (
                    <div />
                )}

                {/* Slot 4: Hapus — selalu tampil */}
                <ActionBtn
                    icon={FaTrashCan}
                    label="Hapus"
                    color="slate"
                    onClick={() => handleOpenDelete(item)}
                />
            </div>
        );
    };

    // ==========================================
    // MOBILE CARD ITEM
    // ==========================================
    const MobileCard = ({ item }) => {
        const isConnected = item?.status === "Connected";
        return (
            <div
                className="rounded-2xl p-4 border transition-all duration-200 hover:border-slate-600/40"
                style={{
                    background: "rgba(15, 23, 42, 0.4)",
                    borderColor: "rgba(51, 65, 85, 0.3)",
                }}
            >
                <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <div
                            className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                                isConnected
                                    ? "bg-emerald-500/10 border border-emerald-500/15"
                                    : "bg-slate-800/60 border border-slate-700/40"
                            }`}
                        >
                            <FaWhatsapp
                                className={`text-lg ${
                                    isConnected
                                        ? "text-emerald-400"
                                        : "text-slate-600"
                                }`}
                            />
                        </div>
                        <div className="min-w-0">
                            <h4 className="text-sm font-bold text-gray-100 truncate">
                                {item?.name || "-"}
                            </h4>
                            <p className="text-xs text-slate-500 font-mono mt-0.5">
                                {formatPhoneNumber(item?.number)}
                            </p>
                        </div>
                    </div>
                    <div className="shrink-0 ml-2">
                        <StatusBadge status={item?.status} />
                    </div>
                </div>

                {/* 4 Tombol Aksi — Mobile */}
                <div className="grid grid-cols-4 gap-2 pt-3 border-t border-slate-800/50">
                    <button
                        onClick={() => handleOpenScan(item)}
                        className={`flex items-center justify-center gap-1 py-2 rounded-xl text-[10px] font-bold transition-all border whitespace-nowrap ${
                            isConnected
                                ? "bg-amber-500/8 text-amber-400 border-amber-500/15 hover:bg-amber-500/15"
                                : "bg-cyan-500/8 text-cyan-400 border-cyan-500/15 hover:bg-cyan-500/15"
                        }`}
                    >
                        <FaQrcode size={10} className="shrink-0" />
                        <span>{isConnected ? "Scan" : "Scan"}</span>
                    </button>
                    <button
                        onClick={() => handleOpenUpdate(item)}
                        className="flex items-center justify-center gap-1 py-2 rounded-xl text-[10px] font-bold transition-all border bg-blue-500/8 text-blue-400 border-blue-500/15 hover:bg-blue-500/15"
                    >
                        <FaPenToSquare size={10} className="shrink-0" />
                        <span>Update</span>
                    </button>
                    {isConnected ? (
                        <button
                            onClick={() => handleOpenLogout(item)}
                            className="flex items-center justify-center gap-1 py-2 rounded-xl text-[10px] font-bold transition-all border bg-amber-500/8 text-amber-400 border-amber-500/15 hover:bg-amber-500/15"
                        >
                            <FaRightFromBracket
                                size={10}
                                className="shrink-0"
                            />
                            <span>Logout</span>
                        </button>
                    ) : (
                        <div />
                    )}
                    <button
                        onClick={() => handleOpenDelete(item)}
                        className="flex items-center justify-center gap-1 py-2 rounded-xl text-[10px] font-bold transition-all border bg-slate-700/30 text-slate-400 border-slate-600/30 hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/20"
                    >
                        <FaTrashCan size={10} className="shrink-0" />
                        <span>Hapus</span>
                    </button>
                </div>
            </div>
        );
    };

    // ==========================================
    // MAIN RENDER
    // ==========================================
    return (
        <div className="space-y-4">
            <ScanModal />
            <LogoutModal />
            <UpdateModal />
            <DeleteModal />

            {/* ==================== DESKTOP TABLE ==================== */}
            <section
                className="hidden lg:block rounded-2xl overflow-hidden"
                style={{
                    background: "rgba(15, 23, 42, 0.4)",
                    border: "1px solid rgba(51, 65, 85, 0.3)",
                    backdropFilter: "blur(12px)",
                }}
                aria-label="Daftar Perangkat WhatsApp"
            >
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr
                                className="border-b"
                                style={{
                                    background: "rgba(15, 23, 42, 0.6)",
                                    borderColor: "rgba(51, 65, 85, 0.3)",
                                }}
                            >
                                <th className="px-4 py-3.5 text-center w-12">
                                    <input
                                        type="checkbox"
                                        checked={
                                            data?.length > 0 &&
                                            selectedIds.length === data.length
                                        }
                                        onChange={handleSelectAll}
                                        className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500/30 focus:ring-offset-0 cursor-pointer"
                                    />
                                </th>
                                {deviceColumns.map((col) => (
                                    <th
                                        key={col.key}
                                        className={`px-4 py-3.5 text-[10px] font-bold uppercase tracking-widest text-slate-500 ${
                                            col.align === "center"
                                                ? "text-center"
                                                : "text-left"
                                        }`}
                                        style={
                                            col.width
                                                ? { width: col.width }
                                                : undefined
                                        }
                                    >
                                        {col.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>

                        {loading ? (
                            <TableSkeleton />
                        ) : !data || data.length === 0 ? (
                            <tbody>
                                <tr>
                                    <td colSpan={deviceColumns.length + 1}>
                                        <EmptyState />
                                    </td>
                                </tr>
                            </tbody>
                        ) : (
                            <tbody>
                                {data.map((item, index) => {
                                    const isConnected =
                                        item?.status === "Connected";
                                    return (
                                        <tr
                                            key={item.id}
                                            className="group transition-colors duration-150 border-b border-slate-800/30 last:border-0"
                                            style={{
                                                background: "transparent",
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.background =
                                                    "rgba(0, 180, 216, 0.03)";
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.background =
                                                    "transparent";
                                            }}
                                        >
                                            <td
                                                className="px-4 py-3.5 text-center"
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedIds.includes(
                                                        item.id
                                                    )}
                                                    onChange={() =>
                                                        handleToggleSelect(
                                                            item.id
                                                        )
                                                    }
                                                    className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500/30 focus:ring-offset-0 cursor-pointer"
                                                />
                                            </td>

                                            <td className="px-4 py-3.5 text-center text-slate-600 font-mono text-xs">
                                                {index + 1}
                                            </td>

                                            <td className="px-4 py-3.5">
                                                <div className="flex items-center gap-2.5">
                                                    <div
                                                        className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                                                            isConnected
                                                                ? "bg-emerald-500/8 border border-emerald-500/15"
                                                                : "bg-slate-800/60 border border-slate-700/40"
                                                        }`}
                                                    >
                                                        <FaWhatsapp
                                                            className={`text-sm ${
                                                                isConnected
                                                                    ? "text-emerald-400"
                                                                    : "text-slate-600"
                                                            }`}
                                                        />
                                                    </div>
                                                    <span className="font-semibold text-gray-100 text-sm">
                                                        {item?.name || "-"}
                                                    </span>
                                                </div>
                                            </td>

                                            <td className="px-4 py-3.5">
                                                <span className="font-mono text-sm text-slate-300">
                                                    {formatPhoneNumber(
                                                        item?.number
                                                    )}
                                                </span>
                                            </td>

                                            <td className="px-4 py-3.5 text-center">
                                                <StatusBadge
                                                    status={item?.status}
                                                />
                                            </td>

                                            <td
                                                className="px-3 py-3.5 text-center"
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                            >
                                                <ActionGroup item={item} />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        )}
                    </table>
                </div>
            </section>

            {/* ==================== MOBILE / TABLET CARDS ==================== */}
            <section
                className="lg:hidden space-y-3"
                aria-label="Daftar Perangkat WhatsApp"
            >
                {loading ? (
                    <div className="space-y-3">
                        <CardSkeleton />
                        <CardSkeleton />
                        <CardSkeleton />
                    </div>
                ) : !data || data.length === 0 ? (
                    <div
                        className="rounded-2xl border"
                        style={{
                            background: "rgba(15, 23, 42, 0.4)",
                            borderColor: "rgba(51, 65, 85, 0.3)",
                        }}
                    >
                        <EmptyState />
                    </div>
                ) : (
                    <div className="space-y-3">
                        {data.map((item, index) => (
                            <MobileCard
                                key={item.id}
                                item={item}
                                index={index}
                            />
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
};